"use client";

import { useRef, useSyncExternalStore } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import SupplierPreview, {
  type Supplier,
} from "./SupplierPreview";

import styles from "./home.module.css";

type Props = {
  suppliers: Supplier[];
  initialUrls?: Record<number, string>;
};

export default function SupplierCarousel({
  suppliers,
  initialUrls,
}: Props) {
  const carouselRef = useRef<HTMLDivElement>(null);

  const hydrated = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  const orderedSuppliers = useRef<Supplier[] | null>(null);

  if (hydrated && orderedSuppliers.current === null) {
    const shuffled = [...suppliers];

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    orderedSuppliers.current = shuffled;
  }

  const visibleSuppliers = orderedSuppliers.current ?? suppliers;


  const dragRef = useRef({
    active: false,
    startX: 0,
    startScroll: 0,
    moved: false,
  });

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement>) {
    if (event.button !== 0) return;

    const element = carouselRef.current;
    if (!element) return;

    dragRef.current = {
      active: true,
      startX: event.clientX,
      startScroll: element.scrollLeft,
      moved: false,
    };

    element.style.cursor = "grabbing";
  }

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    const element = carouselRef.current;

    if (!drag.active || !element) return;

    const distance = event.clientX - drag.startX;

    if (Math.abs(distance) > 5) {
      drag.moved = true;
    }

    if (!drag.moved) return;

    event.preventDefault();
    element.style.scrollBehavior = "auto";
    element.style.scrollSnapType = "none";
    element.scrollLeft = drag.startScroll - distance;
  }

  function handleMouseUp() {
    const element = carouselRef.current;

    dragRef.current.active = false;

    if (element) {
      element.style.cursor = "grab";
      element.style.scrollBehavior = "";
      element.style.scrollSnapType = "";
    }
  }

  function handleClickCapture(event: React.MouseEvent<HTMLDivElement>) {
    if (dragRef.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      dragRef.current.moved = false;
    }
  }

  function scroll(direction: number) {
    const element = carouselRef.current;

    if (!element) return;

    element.scrollBy({
      left: direction * element.clientWidth,
      behavior: "smooth",
    });
  }

  return (
    <div className="relative">
      <div
        ref={carouselRef}
        className={styles.supplierCarousel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClickCapture={handleClickCapture}
        aria-label="Carrossel de empresas"
      >
        <SupplierPreview
          suppliers={visibleSuppliers}
          compact
          initialUrls={initialUrls}
        />
      </div>

      {suppliers.length > 4 && (
        <>
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label="Empresas anteriores"
            className="absolute -left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-md transition-colors hover:bg-blue-50"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            type="button"
            onClick={() => scroll(1)}
            aria-label="Próximas empresas"
            className="absolute -right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-md transition-colors hover:bg-blue-50"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}
    </div>
  );
}
